import { Capacitor } from '@capacitor/core';
import api from '../api';
import { generateId } from '../utils/id';
import {
  canWriteWithSubscription,
  getStoredSubscriptionExpiresAt,
} from '../utils/academicYear';
import { addToOutbox, listOutbox, removeFromOutbox, updateOutboxEntry } from './store';

let flushInFlight = null;

/** Web: navigator.onLine. Native: always try API (WebView onLine is unreliable). */
export function isOnline() {
  if (typeof navigator === 'undefined') return true;
  if (Capacitor.isNativePlatform()) return true;
  return navigator.onLine;
}

function outboxDedupeKey(entry) {
  const classId = entry?.payload?.class_id;
  const date = entry?.payload?.date || '';
  return `${entry.type}:${classId}:${date}`;
}

function mergeNutritionPayloads(entries) {
  const merged = {};
  const sorted = [...entries].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  for (const entry of sorted) {
    const payload = entry.payload || {};
    for (const [key, value] of Object.entries(payload)) {
      if (key === 'class_id' || key === 'date') continue;
      merged[key] = value;
    }
  }
  const base = sorted[sorted.length - 1]?.payload || {};
  return {
    class_id: base.class_id,
    date: base.date,
    ...merged,
  };
}

/** Keep newest pending item per class+date (+ merge nutrition partial saves). */
async function coalescePendingOutbox(pending) {
  const groups = new Map();
  for (const entry of pending) {
    const key = outboxDedupeKey(entry);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  }

  for (const [, entries] of groups) {
    if (entries.length <= 1) continue;
    entries.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    const keep = entries[entries.length - 1];
    const removeIds = entries.slice(0, -1).map((e) => e.id);
    for (const id of removeIds) {
      await removeFromOutbox(id);
    }
    if (keep.type === 'teacher.updateNutrition') {
      const mergedPayload = mergeNutritionPayloads(entries);
      await updateOutboxEntry(keep.id, { payload: mergedPayload });
      keep.payload = mergedPayload;
    }
  }

  return (await listOutbox()).filter((e) => e.status === 'pending');
}

function isPermanentSyncError(message) {
  const msg = String(message || '').toLowerCase();
  return (
    msg.includes('unauthorized') ||
    msg.includes('class not found') ||
    msg.includes('invalid nutrition payload') ||
    msg.includes('unsupported operation') ||
    msg.includes('missing_mutation_id')
  );
}

export async function queueNutritionUpdate(payload) {
  const expiresAt = getStoredSubscriptionExpiresAt();
  if (!canWriteWithSubscription(expiresAt)) {
    throw new Error('SUBSCRIPTION_EXPIRED');
  }
  const id = generateId();
  await addToOutbox({
    id,
    type: 'teacher.updateNutrition',
    payload,
    clientMutationId: id,
    createdAt: Date.now(),
    status: 'pending',
  });
  return id;
}

export async function queueStrengthUpdate(payload) {
  const expiresAt = getStoredSubscriptionExpiresAt();
  if (!canWriteWithSubscription(expiresAt)) {
    throw new Error('SUBSCRIPTION_EXPIRED');
  }
  const id = generateId();
  await addToOutbox({
    id,
    type: 'teacher.updateStrength',
    payload,
    clientMutationId: id,
    createdAt: Date.now(),
    status: 'pending',
  });
  return id;
}

async function doFlushOutbox() {
  if (!isOnline()) {
    return { flushed: 0, pending: (await listOutbox()).filter((i) => i.status === 'pending').length, ok: true };
  }

  const expiresAt = getStoredSubscriptionExpiresAt();
  if (!canWriteWithSubscription(expiresAt)) {
    const pending = (await listOutbox()).filter((e) => e.status === 'pending').length;
    return {
      flushed: 0,
      pending,
      skipped: 'subscription_expired',
      ok: false,
      error: 'SUBSCRIPTION_EXPIRED',
    };
  }

  let pending = (await listOutbox()).filter((e) => e.status === 'pending');
  if (pending.length === 0) {
    return { flushed: 0, pending: 0, ok: true };
  }

  pending = await coalescePendingOutbox(pending);

  const operations = pending.map((entry) => ({
    type: entry.type,
    payload: entry.payload,
    clientMutationId: entry.clientMutationId || entry.id,
  }));

  try {
    const res = await api.post('/sync/push', { operations });
    const results = res.data?.results || [];

    let flushed = 0;
    let firstError = null;

    for (const result of results) {
      const mutationId = result.clientMutationId;
      if (result.success && mutationId) {
        await removeFromOutbox(mutationId);
        flushed += 1;
        continue;
      }

      const errMsg = result.error || 'Sync failed';
      if (!firstError) firstError = errMsg;

      if (mutationId && isPermanentSyncError(errMsg)) {
        await removeFromOutbox(mutationId);
        flushed += 1;
      } else if (mutationId) {
        await updateOutboxEntry(mutationId, {
          status: 'pending',
          lastError: errMsg,
          lastAttemptAt: Date.now(),
        });
      }
    }

    const remaining = await listOutbox();
    const remainingPending = remaining.filter((i) => i.status === 'pending').length;
    const failed = results.filter((r) => !r.success);

    return {
      ok: failed.length === 0,
      flushed,
      pending: remainingPending,
      results,
      partialFailure: failed.length > 0,
      error: firstError,
    };
  } catch (error) {
    const status = error.response?.status;
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Sync failed';

    console.warn('Offline sync push failed:', status, message);

    const remaining = await listOutbox();
    const remainingPending = remaining.filter((i) => i.status === 'pending').length;

    return {
      ok: false,
      flushed: 0,
      pending: remainingPending,
      status,
      error: message,
    };
  }
}

export async function flushOutbox() {
  if (flushInFlight) {
    return flushInFlight;
  }

  flushInFlight = doFlushOutbox();
  try {
    return await flushInFlight;
  } finally {
    flushInFlight = null;
  }
}

export async function getPendingCount() {
  const items = await listOutbox();
  return items.filter((i) => i.status === 'pending').length;
}
